import { translateText, translateTexts } from '@/api/translation.api';
import { useLanguage } from '@/contexts/LanguageContext';
import { LOCALES } from '@/i18n';
import { SupportedLocale } from '@/types/supported-locale.type';
import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  Control,
  FieldValues,
  get,
  Path,
  UseFormSetValue,
  PathValue,
} from 'react-hook-form';

export const useTranslate = (
  targetLanguage: SupportedLocale,
  sourceLanguage?: SupportedLocale,
) =>
  useMutation({
    mutationFn: (text: string) =>
      translateText(text, targetLanguage, sourceLanguage),
  });

export const useTranslateMany = (
  targetLanguage: SupportedLocale,
  sourceLanguage?: SupportedLocale,
) =>
  useMutation({
    mutationFn: (texts: string[]) =>
      translateTexts(texts, targetLanguage, sourceLanguage),
  });

interface UseTranslateFieldsProps<
  FieldsType extends FieldValues,
  TFields extends readonly string[],
> {
  targetLanguage: SupportedLocale;
  control: Control<FieldsType>;
  setValue: UseFormSetValue<FieldsType>;
  fields: TFields;
  nestedFields?: Record<string, readonly string[]>;
  doublyNestedFields?: Record<string, Record<string, readonly string[]>>;
}

export const useTranslateFields = <
  FieldsType extends FieldValues,
  TFields extends readonly string[],
>({
  control,
  setValue,
  fields,
  nestedFields,
  doublyNestedFields,
  targetLanguage,
}: UseTranslateFieldsProps<FieldsType, TFields>) => {
  const { currentLanguage } = useLanguage();
  const { mutateAsync: translateMany, isPending } = useTranslateMany(
    targetLanguage,
    currentLanguage,
  );
  const { mutateAsync: translateToFr, isPending: isTranslatingToFr } =
    useTranslateMany('fr', currentLanguage);
  const { mutateAsync: translateToEn, isPending: isTranslatingToEn } =
    useTranslateMany('en', currentLanguage);
  const { mutateAsync: translateToDe, isPending: isTranslatingToDe } =
    useTranslateMany('de', currentLanguage);
  const { mutateAsync: translateToIt, isPending: isTranslatingToIt } =
    useTranslateMany('it', currentLanguage);
  const { mutateAsync: translateToEs, isPending: isTranslatingToEs } =
    useTranslateMany('es', currentLanguage);

  const translateToLocale = useCallback(
    (locale: SupportedLocale) => {
      switch (locale) {
        case 'fr':
          return translateToFr;
        case 'en':
          return translateToEn;
        case 'de':
          return translateToDe;
        case 'it':
          return translateToIt;
        case 'es':
          return translateToEs;
      }
    },
    [translateToDe, translateToEn, translateToEs, translateToFr, translateToIt],
  );

  const translateFields = useCallback(
    async ({
      fieldsToTranslate,
      nestedFieldsToTranslate = {},
      allNestedFields = false,
      translateToAllLocales = false,
    }: {
      fieldsToTranslate?: TFields[number][];
      nestedFieldsToTranslate?: Record<string, readonly string[]>;
      allNestedFields?: boolean;
      translateToAllLocales?: boolean;
    }) => {
      if (!translateToAllLocales && currentLanguage === targetLanguage) return;

      const targetFields = fieldsToTranslate ?? fields;

      type Task = { value: string; targetPath: Path<FieldsType> };
      const tasks: Task[] = [];

      // Top-level fields
      targetFields.forEach((fieldName) => {
        const currentValuePath =
          `translations.${currentLanguage}.${fieldName}` as Path<FieldsType>;
        const targetValuePath =
          `translations.${targetLanguage}.${fieldName}` as Path<FieldsType>;
        const currentValue = get(control._formValues, currentValuePath);
        if (
          currentValue &&
          typeof currentValue === 'string' &&
          currentValue.trim()
        ) {
          tasks.push({ value: currentValue, targetPath: targetValuePath });
        }
      });

      // Nested arrays
      const targetNestedFields = allNestedFields
        ? (nestedFields ?? {})
        : nestedFieldsToTranslate;

      Object.entries(targetNestedFields).forEach(([arrayPath, itemFields]) => {
        const currentArrayPath =
          `translations.${currentLanguage}.${arrayPath}` as Path<FieldsType>;
        const targetArrayBasePath = `translations.${targetLanguage}.${arrayPath}`;
        const currentArray = get(
          control._formValues,
          currentArrayPath,
        ) as unknown;
        if (!Array.isArray(currentArray)) return;

        currentArray.forEach((item, index) => {
          itemFields.forEach((field) => {
            const value = (item as Record<string, unknown>)?.[field];
            if (typeof value === 'string' && value.trim()) {
              const targetItemFieldPath =
                `${targetArrayBasePath}.${index}.${field}` as Path<FieldsType>;
              tasks.push({ value, targetPath: targetItemFieldPath });
            }
          });
        });
      });

      // Doubly nested arrays
      if (translateToAllLocales)
        Object.entries(doublyNestedFields ?? {}).forEach(
          ([parentArrayPath, subArrayMap]) => {
            const currentParentArrayPath =
              `translations.${currentLanguage}.${parentArrayPath}` as Path<FieldsType>;
            const targetParentArrayBasePath = `translations.${targetLanguage}.${parentArrayPath}`;
            const parentArray = get(
              control._formValues,
              currentParentArrayPath,
            ) as unknown;
            if (!Array.isArray(parentArray)) return;

            parentArray.forEach((parentItem, parentIndex) => {
              Object.entries(subArrayMap).forEach(
                ([subArrayKey, subItemFields]) => {
                  const subArray = (parentItem as Record<string, unknown>)?.[
                    subArrayKey
                  ] as unknown;
                  if (!Array.isArray(subArray)) return;

                  subArray.forEach((subItem, subIndex) => {
                    subItemFields.forEach((field) => {
                      const value = (subItem as Record<string, unknown>)?.[
                        field
                      ];
                      if (typeof value === 'string' && value.trim()) {
                        const targetItemFieldPath =
                          `${targetParentArrayBasePath}.${parentIndex}.${subArrayKey}.${subIndex}.${field}` as Path<FieldsType>;
                        tasks.push({ value, targetPath: targetItemFieldPath });
                      }
                    });
                  });
                },
              );
            });
          },
        );

      if (tasks.length === 0) return;

      // Batch translate and map back by index
      try {
        if (!translateToAllLocales) {
          const results = await translateMany(tasks.map((t) => t.value));
          results.forEach((r, i) => {
            setValue(
              tasks[i].targetPath,
              r.text as PathValue<FieldsType, Path<FieldsType>>,
            );
          });
        } else {
          const getPathForLocale = (
            field: Path<FieldsType>,
            locale: SupportedLocale,
          ) =>
            field.replace(
              `translations.${targetLanguage}`,
              `translations.${locale}`,
            ) as Path<FieldsType>;

          await Promise.all(
            LOCALES.filter((l) => l !== currentLanguage).map(async (locale) => {
              const results = await translateToLocale(locale)(
                tasks.map((t) => t.value),
              );
              results.forEach((r, i) => {
                const path = getPathForLocale(tasks[i].targetPath, locale);
                setValue(
                  path,
                  r.text as PathValue<FieldsType, Path<FieldsType>>,
                );
              });
              return results;
            }),
          );
        }
      } catch (error) {
        console.error('Failed to batch translate fields:', error);
      }
    },
    [
      control._formValues,
      currentLanguage,
      doublyNestedFields,
      fields,
      nestedFields,
      setValue,
      targetLanguage,
      translateMany,
      translateToLocale,
    ],
  );

  return {
    translateFields,
    isTranslating:
      isPending ||
      isTranslatingToFr ||
      isTranslatingToEn ||
      isTranslatingToDe ||
      isTranslatingToIt ||
      isTranslatingToEs,
  };
};
